import Styles from './styles.module.css';
import React, { useState } from 'react';
import ErrorMessage from '../../../../package/error/index';
import DefaultInput1 from '../../../../package/1-input/index';
import DefaultTextarea1 from '../../../../package/1-textarea/index';
import { doc, getFirestore, updateDoc } from 'firebase/firestore';
import { summaryData, ValidationError } from 'src/type';
import Hint from './hint';

type JobPostingsSummaryProps = {
  documentId: string;
  setChangeData: React.Dispatch<React.SetStateAction<boolean>>;
  summary: summaryData;
  setSummary: React.Dispatch<React.SetStateAction<summaryData>>;
};

const JobPostingsSummary: React.FC<JobPostingsSummaryProps> = ({ documentId, setChangeData, summary, setSummary }) => {
  
  const db = getFirestore();

  const [ errorMessage, setErrorMessage ] = useState<ValidationError>(null);
  const [ banner, setBanner ] = useState(false);
  const [ hintText, setHintText ] = useState<{ title: string; desc: string }>({ title: '', desc: '' });

  /** =========================
   * 共通更新処理
   * ========================= */
  const updateField = <K extends keyof summaryData>(
    key: K,
    value: summaryData[K]
  ) => {
    setSummary((prev) => ({ ...prev, [key]: value }));
    setChangeData(true);
    setErrorMessage(null);
  };

  /** =========================
   * バリデーション
   * ========================= */
  const validate = (data: summaryData): ValidationError => {
    if (!data.name) return { message: 'サロン名を入力してください。', fieldId: 'name' };
    if (!data.title) return { message: '求人タイトルを入力してください。', fieldId: 'title' };
    if (!data.companyPr) return { message: '企業の特徴を入力してください。', fieldId: 'companyPr' };
    return null;
  };

  /** =========================
   * スクロール制御
   * ========================= */
  const scrollToField = (fieldId: string) => {
    const el = document.getElementById(fieldId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    (el as HTMLElement).focus?.();
  };

  /** =========================
   * 保存処理
   * ========================= */
  const [ uploading, setUploading ] = useState(false);

  const save = async (isCompleted: boolean) => {
    try {
      setUploading(true);

      console.log('[debug] recruits summary update 開始', documentId); // 一時デバッグ用
      await updateDoc(doc(db, 'recruits', documentId), {
        summary: {
          ...summary,
          isCompleted,
          time: new Date(),
        },
      });

      setChangeData(false);
    } catch (e) {
      setUploading(false);
      console.error(e);
    } finally {
      setUploading(false);
      setBanner(true);
      setTimeout(() => setBanner(false), 1500);
    }
  };

  /** =========================
   * 保存（バリデーションあり）
   * ========================= */
  const handleSubmit = () => {
    setErrorMessage(null);
    window.scrollTo(0, 0);

    const error = validate(summary);
    if (error) {
      setErrorMessage(error);
      scrollToField(error.fieldId);
      return;
    }

    save(true);
  };

  return (
    <>
      {errorMessage && <ErrorMessage message={errorMessage.message} />}
      {banner && <div className={Styles.banner}>保存しました。</div>}
      {hintText.title && <Hint contents={hintText} setHintText={setHintText} />}

      {uploading && (
        <div className={Styles.lockOverlay}>
          <div className={Styles.spinner} />
          <p>アップロード中...</p>
        </div>
      )}

      <div className={Styles.title_edit}>
        <h4>魅力の編集</h4>
        <div className={Styles.title_edit_btn}>
          <button className={Styles.save_box_now} onClick={() => save(false)}>
            一時保存
          </button>
          <button className={Styles.save_box_save} onClick={handleSubmit}>
            保存
          </button>
        </div>
      </div>

      <div className={Styles.test_box}>
        <DefaultInput1
          error={errorMessage?.fieldId === 'name'}
          title="サロン名"
          length={20}
          place="memorii美容院、memorii美容院 横浜店"
          value={summary.name}
          setValue={(val) => updateField('name', val)}
          desc=""
          hint={{ title: '', desc: '' }}
          setHintText={setHintText}
        />

        <div id="title" tabIndex={1} />
        <DefaultInput1
          error={errorMessage?.fieldId === 'title'}
          title="求人タイトル"
          length={30}
          place="成長できる環境とあたたかな人間関係が魅力"
          value={summary.title}
          setValue={(val) => updateField('title', val)}
          hint={{
            title: 'タイトルの付け方',
            desc: '学生検索に表示されるため30文字フル活用推奨',
          }}
          setHintText={setHintText}
        />

        <div id="companyPr" tabIndex={-1} />
        <DefaultTextarea1
          error={errorMessage?.fieldId === 'companyPr'}
          title="企業の特徴"
          length={100}
          place="地域で愛されるアットホームなサロンです。"
          value={summary.companyPr}
          setValue={(val) => updateField('companyPr', val)}
          hint={{
            title: '特徴の記載方法',
            desc: '検索対象になるため100文字推奨',
          }}
          setHintText={setHintText}
        />
      </div>
    </>
  );
};

export default JobPostingsSummary;